const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/labReportsActions.ts';
import { KISAPI as API } from '../middleware/kisapi';
import { IKISAPIAction, IKISAPIMeta, KisApiAction, kisapiActionTypes } from '../defs/actionTypes';
import { LabCumulDataSource } from '../defs/labReportDefs';
import { getMainConfig } from '../services/mainConfig';
import { apiLoad1LabSingleReport, apiLoadLabSingleReportRights } from '@cistec/common_web/actions/kisapi/reports';
import {
    apiLoadCumulDefsExtras,
    apiLoadLabNewCumulData,
    apiLoadLabNewCumulSummary,
    apiLoadLabRepMapData,
    apiLoadLabRepMapSummary,
} from '@cistec/common_web/actions/kisapi/labCumul';
import { apiLoad1MetaKatRecord, apiLoadMetaKatBlock } from '@cistec/common_web/actions/kisapi/metaKat';

export const actionTypes = {
    loadCumulConfig: kisapiActionTypes('COMMON_LOAD_CUMUL_CONFIG'),
    loadCumulDefs: kisapiActionTypes('COMMON_LOAD_CUMUL_DEFS'),
    loadCumulDefsExtras: kisapiActionTypes('COMMON_LOAD_CUMUL_DEFS_EXTRAS'),
    aboutToLoadCumulSummary: 'COMMON_ABOUT_TO_LOAD_CUMUL_SUMMARY',
    completedLoadingCumulSummary: 'COMMON_COMPLETED_LOADING_CUMUL_SUMMARY',
    abortLoadingCumulSummary: 'COMMON_ABORT_LOADING_CUMUL_SUMMARY',
    resumeLoadingCumulSummary: 'COMMON_RESUME_LOADING_CUMUL_SUMMARY',
    loadCumulSummaryStart: 'COMMON_START_LOAD_CUMUL_SUMMARY',
    loadCumulSummary: kisapiActionTypes('COMMON_LOAD_CUMUL_SUMMARY'),
    loadCumulDataStart: 'COMMON_START_LOAD_CUMUL_DATA',
    loadCumulData: kisapiActionTypes('COMMON_LOAD_CUMUL_DATA'),
    extendCumulAvailLimit: 'COMMON_EXTEND_CUMUL_AVAIL_LIMIT',
    adjustVisibleMaxDat: 'COMMON_CUMUL_ADJUST_VISIBLE_MAXDAT',
    setDesiredNumColumns: 'COMMON_CUMUL_SET_DESIRED_NUM_COLUMNS',
    setFetchOptions: 'COMMON_CUMUL_SET_FETCH_OPTIONS',
    reload1CumulReport: 'COMMON_RELOAD_ONE_CUMUL_REPORT',
    truncate1CumulReport: 'COMMON_TRUNCATE_ONE_CUMUL_REPORT',
    forget1CumulReport: 'COMMON_FORGET_ONE_CUMUL_REPORT',
    loadLabSingleReports: kisapiActionTypes('COMMON_LOAD_LAB_SINGLE_REPORTS'),
    load1LabSingleReport: kisapiActionTypes('COMMON_LOAD_ONE_LAB_SINGLE_REPORT'),
    updateLabSingleReport: kisapiActionTypes('COMMON_UPDATE_LAB_SINGLE_REPORT'),
    loadLabSingleRepRights: kisapiActionTypes('COMMON_LOAD_LAB_SINGLE_REP_RIGHTS'),
};

export interface LoadCumulSummaryMeta extends IKISAPIMeta {
    instanceId: string;
    patnr: string;
    defsId: string;
    mindat: string;
    maxdat: string;
    dataSource?: LabCumulDataSource;
    configIcode?: string;
    filterIcode?: string;
    paramFcode?: string;
    mapIcode?: string;
}

export interface LoadCumulDataMeta extends IKISAPIMeta {
    instanceId: string;
    patnr: string;
    defsId: string;
    sender: string;
    reportnr: string;
    dataSource?: LabCumulDataSource;
    configIcode?: string;
    filterIcode?: string;
    mapIcode?: string;
}

export interface LoadLabSingleRepsAction extends IKISAPIAction {
    meta: IKISAPIMeta & {
        evenIfDeleted?: boolean;
        patnr: string;
    };
}

export interface UpdateLabSingleReportAction extends IKISAPIAction {
    meta: IKISAPIMeta & {
        sender: string;
        reportnr: string;
    };
}

/**
 * Causes the lab-reports configuration to be loaded from MetaKat.
 */
export function loadCumulConfig(icode: string, callerId?: string, doNotAttemptToLoadCumulDefs?: boolean) {
    return {
        type: actionTypes.loadCumulConfig.combined,
        [API]: apiLoad1MetaKatRecord(icode, ['KAT', 'FKEY', 'NAME', 'CONTENT']),
        meta: { callerId, doNotAttemptToLoadCumulDefs },
    };
}

export type LoadCumulConfigAction = KisApiAction<typeof loadCumulConfig>;

/**
 * Action to be used by labReportsLoadCumulDefsSaga to load the cumulDefs.
 * @param callerId Optional id to identify the calling instance
 * @return: An action object
 */
export function loadCumulDefs(callerId?: string) {
    const pcode = getMainConfig().settings.labCumulDefsPcode;

    if (!pcode) {
        throw new Error('labCumulDefsPcode not set!');
    }

    return {
        type: actionTypes.loadCumulDefs.combined,
        [API]: apiLoadMetaKatBlock(pcode, ['KAT', 'ICODE', 'NAME', 'POS', 'CHILD'], false, true),
        meta: {
            callerId,
            pcode,
            showDlgWhenResultHasMoreRows: true,
        },
    };
}

export type LoadCumulDefsAction = KisApiAction<typeof loadCumulDefs>;

/**
 * Loads additional information to be integrated into the cumulDefs.
 * This action is not invoked when the hospital doesn't use the new Kumulativbefund.
 */
export function loadCumulDefsExtras(filterIcode: string) {
    const meta = { filterIcode };
    return {
        type: actionTypes.loadCumulDefsExtras.combined,
        [API]: apiLoadCumulDefsExtras(filterIcode),
        meta,
    };
}

export type LoadCumulDefsExtrasAction = KisApiAction<typeof loadCumulDefsExtras>;

/**
 * Before invoking the action loadCumulSummary, the loadSummariesSaga will
 * first invoke this action. The reducer will mark the report as loading.
 * Be sure to ALWAYS balance a call to this method with a corresponding call
 * to completedLoadingCumulSummary.
 */
export function aboutToLoadCumulSummary(instanceId: string, patnr: string, defsId: string) {
    return {
        type: actionTypes.aboutToLoadCumulSummary,
        instanceId,
        patnr,
        defsId,
    };
}

export type AboutToLoadCumulSummaryAction = ReturnType<typeof aboutToLoadCumulSummary>;

/**
 * The loadSummariesSaga will invoke this action when the chain of KISAPI calls
 * ends, either because enough results were found, or because some limit was
 * reached.
 */
export function completedLoadingCumulSummary(instanceId: string, patnr: string, defsId: string) {
    return {
        type: actionTypes.completedLoadingCumulSummary,
        instanceId,
        patnr,
        defsId,
    };
}

export type CompletedLoadingCumulSummaryAction = ReturnType<typeof completedLoadingCumulSummary>;

/**
 * The loadSummariesSaga will invoke this action when the chain of KISAPI calls is aborted before
 * the task has completed.
 */
export function abortLoadingCumulSummary(instanceId: string, patnr: string, defsId: string) {
    return {
        type: actionTypes.abortLoadingCumulSummary,
        instanceId,
        patnr,
        defsId,
    };
}

export type AbortLoadingCumulSummaryAction = ReturnType<typeof abortLoadingCumulSummary>;

/**
 * This action tells the summary saga to resume operation after it has stopped without completing
 * its task, e.g. because of a failed KISAPI call.
 */
export function resumeLoadingCumulSummary(instanceId: string, patnr: string, defsId: string) {
    return {
        type: actionTypes.resumeLoadingCumulSummary,
        instanceId,
        patnr,
        defsId,
    };
}

export type ResumeLoadingCumulSummaryAction = ReturnType<typeof resumeLoadingCumulSummary>;

/**
 * Action to set the avail limit to some new date.
 * @param instanceId id of the lab reports instance
 * @param patnr patient id
 * @param defsId id of the report's cumulDefs
 * @param availLimit Date, YYYY-MM-DD HH:mm:ss, to check as a new limit. The action will do
 *    nothing if the given availLimit is newer than the existing availLimit. If the argument
 *    is not set, the availLimit will be automatically extended.
 */
export function extendCumulAvailLimit(instanceId: string, patnr: string, defsId: string, availLimit?: string) {
    return {
        type: actionTypes.extendCumulAvailLimit,
        instanceId,
        patnr,
        defsId,
        availLimit,
    };
}

export type ExtendCumulAvailLimitAction = ReturnType<typeof extendCumulAvailLimit>;

/**
 * Action to tell the adjustVisibleMaxDatSaga to become active.
 */
export function adjustVisibleMaxDat(instanceId: string, patnr: string, defsId: string) {
    return {
        type: actionTypes.adjustVisibleMaxDat,
        instanceId,
        patnr,
        defsId,
    };
}

// noinspection JSUnusedGlobalSymbols
export type AdjustVisibleMaxDatAction = ReturnType<typeof adjustVisibleMaxDat>;

/**
 * Action to look for data of a given cumulative report.
 * @param instanceId id of the lab reports instance
 * @param patnr the patient whose lab reports are consulted
 * @param defsId the id of the cumulDefs of the report of interest
 * @param mindat the time window's lower boundary
 * @param maxdat the time window's upper boundary
 */
export function loadCumulSummary(
    instanceId: string,
    patnr: string,
    defsId: string,
    mindat: string | undefined,
    maxdat: string | undefined
) {
    return {
        type: actionTypes.loadCumulSummaryStart,
        meta: { instanceId, patnr, defsId, mindat, maxdat },
    };
}

export type LoadCumulSummaryAction = ReturnType<typeof loadCumulSummary> & KisApiAction;

/**
 * Invoked by loadSummariesSaga for new cumulative reports
 */
export function loadLabNewCumulSummary(meta: LoadCumulSummaryMeta) {
    if (!meta.configIcode) {
        throw new Error('Cannot load labreport summary with new endpoint: configIcode missing!');
    }

    if (!meta.filterIcode) {
        throw new Error('Cannot load labreport summary with new endpoint: filterIcode missing!');
    }

    return {
        type: actionTypes.loadCumulSummary.combined,
        [API]: apiLoadLabNewCumulSummary(meta.patnr, meta.configIcode, meta.filterIcode, meta.mindat, meta.maxdat),
        meta,
    };
}

export type LoadLabNewCumulSummaryAction = KisApiAction<typeof loadLabNewCumulSummary>;

/**
 * Invoked by loadSummariesSaga for mapping based reports
 */
export function loadLabRepMapSummary(meta: LoadCumulSummaryMeta) {
    if (!meta.mapIcode) {
        throw new Error('Cannot load mapping-based labreport summary: mapIcode missing!');
    }

    return {
        type: actionTypes.loadCumulSummary.combined,
        [API]: apiLoadLabRepMapSummary(meta.patnr, meta.mapIcode, meta.mindat, meta.maxdat),
        meta,
    };
}

/**
 * Action to load the actual data of a given cumulative report. You need to
 * load the summary first before you can call this action.
 * @param instanceId id of the lab reports instance
 * @param patnr the patient whose lab reports are consulted
 * @param defsId the id of the cumulDefs of the report of interest
 * @param sender the sender as retrieved from the summary
 * @param reportnr the reportnr as retrieved from the summary
 */
export function loadCumulData(instanceId: string, patnr: string, defsId: string, sender: string, reportnr: string) {
    const meta: LoadCumulDataMeta = { instanceId, patnr, defsId, sender, reportnr };
    return {
        type: actionTypes.loadCumulDataStart,
        meta,
    };
}

export type LoadCumulDataAction = ReturnType<typeof loadCumulData> & { payload?: any };

/**
 * Invoked by loadCumulDataSaga for new cumulative reports
 */
export function loadLabNewCumulData(meta: LoadCumulDataMeta) {
    if (!meta.configIcode) {
        throw new Error('Cannot load labreport data with new endpoint: configIcode missing!');
    }

    if (!meta.filterIcode) {
        throw new Error('Cannot load labreport data with new endpoint: filterIcode missing!');
    }

    return {
        type: actionTypes.loadCumulData.combined,
        [API]: apiLoadLabNewCumulData(meta.patnr, meta.configIcode, meta.filterIcode, meta.sender, meta.reportnr),
        meta,
    };
}

export type LoadLabNewCumulDataAction = KisApiAction<typeof loadLabNewCumulData>;

/**
 * Invoked by loadCumulDataSaga for mapping-based reports
 */
export function loadLabRepMapData(meta: LoadCumulDataMeta) {
    if (!meta.mapIcode) {
        throw new Error('Cannot load mapping-based labreport data: mapIcode missing!');
    }

    return {
        type: actionTypes.loadCumulData.combined,
        [API]: apiLoadLabRepMapData(meta.patnr, meta.mapIcode, meta.sender, meta.reportnr),
        meta,
    };
}

/**
 * Action to set the desired number of columns to be fetched. Note that this refers to the number of items in the
 * availList rather than to the number columns currently displayed by some component.
 * @param instanceId id of the lab reports instance
 * @param patnr the patient the report belongs to
 * @param defsId id of the cumulDefs
 * @param desiredNumColumns the new number of columns, or 'unlimited' to disable the limit
 * @return: An action object
 */
export function setDesiredNumColumns(
    instanceId: string,
    patnr: string,
    defsId: string,
    desiredNumColumns: number | 'unlimited'
) {
    return {
        type: actionTypes.setDesiredNumColumns,
        instanceId,
        patnr,
        defsId,
        desiredNumColumns,
    };
}

export type SetDesiredNumColumnsAction = ReturnType<typeof setDesiredNumColumns>;

/**
 * Action to set the options how data is fetched.
 * @param instanceId id of the lab reports instance
 * @param initFetchIntervalAmount how far to look back for initial fetch
 * @param initFetchIntervalUnits moment unit, e.g. 'hours', 'minutes', etc.
 */
export function setFetchOptions(instanceId: string, initFetchIntervalAmount: number, initFetchIntervalUnits: string) {
    return {
        type: actionTypes.setFetchOptions,
        instanceId,
        initFetchIntervalAmount,
        initFetchIntervalUnits,
    };
}

export type SetFetchOptionsAction = ReturnType<typeof setFetchOptions>;

/**
 * Action to load a particular single-result lab report
 */
export function loadLabSingleReport(sender: string, reportnr: string) {
    const meta = { sender, reportnr };
    return {
        type: actionTypes.load1LabSingleReport.combined,
        [API]: apiLoad1LabSingleReport(sender, reportnr),
        meta,
    };
}

export type Load1LabSingleReportAction = KisApiAction<typeof loadLabSingleReport>;

/**
 * Action to load the rights specific to a given lab report.
 * @param sender part of the report's primary key
 * @param reportnr part of the report's primary key
 */
export function loadLabSingleRepRights(sender: string, reportnr: string) {
    const meta = {
        sender,
        reportnr,
        suppressErrorMsg: true,
        showDlgWhenResultHasMoreRows: true,
    };

    return {
        type: actionTypes.loadLabSingleRepRights.combined,
        [API]: apiLoadLabSingleReportRights(sender, reportnr),
        meta,
    };
}

export type LoadLabSingleRepRightsAction = KisApiAction<typeof loadLabSingleRepRights>;

/**
 * Works similar to forgetCumulReport, except that this action also tells the sagas to
 * immediately reload report data afterwards.
 */
export function reload1CumulReport(instanceId: string, patnr: string, defsId: string) {
    return {
        type: actionTypes.reload1CumulReport,
        instanceId,
        patnr,
        defsId,
    };
}

export type Reload1CumulReportAction = ReturnType<typeof reload1CumulReport>;

/**
 * Truncates the given report to a maximum number of columns, keeping the youngest few columns.
 */
export function truncate1CumulReport(instanceId: string, patnr: string, defsId: string, maxNumColumns: number) {
    return {
        type: actionTypes.truncate1CumulReport,
        instanceId,
        patnr,
        defsId,
        maxNumColumns,
    };
}

export type Truncate1CumulReportAction = ReturnType<typeof truncate1CumulReport>;

/**
 * Completely forgets about the given cumulative report.
 */
export function forget1CumulReport(instanceId: string, patnr: string, defsId: string) {
    return {
        type: actionTypes.forget1CumulReport,
        instanceId,
        patnr,
        defsId,
    };
}

export type Forget1CumulReportAction = ReturnType<typeof forget1CumulReport>;
